import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { openPrintWindow, esc } from "../lib/print";
import { PageHeader, Card, Btn, Empty } from "../components/UI";
import { FLabel } from "./engagement/FormControls";
import { useClients, ClientSelect } from "./clinical/shared";
import { printLetter, printFaxCover } from "./LettersMessaging";
import { Printer, FileText, User, Send } from "lucide-react";
import { toast } from "sonner";

function printChartSummary(d) {
  const c = d.client;
  const rows = (list, cols) => list.length
    ? `<table><tr>${cols.map(([, h]) => `<th>${h}</th>`).join("")}</tr>${list.map((x) => `<tr>${cols.map(([k]) => `<td>${esc(typeof k === "function" ? k(x) : x[k])}</td>`).join("")}</tr>`).join("")}</table>`
    : `<p style="font-size:12px;color:#A1887F">None on file.</p>`;
  openPrintWindow(`Chart Summary - ${c.first_name} ${c.last_name}`, `
    <div class="clinic"><h1>Client Chart Summary</h1>${esc(d.clinic_name)} · Generated ${dayjs(d.generated_at).format("MMMM D, YYYY HH:mm")} by ${esc(d.generated_by)}</div>
    <div class="row">
      <div><div class="label">Client</div><div class="value">${esc(c.first_name)} ${esc(c.last_name)}</div>
        <div class="label">MRN</div><div class="value">${esc(c.mrn)}</div></div>
      <div><div class="label">DOB</div><div class="value">${esc(c.dob) || "-"}</div>
        <div class="label">Status</div><div class="value">${esc(c.status) || "active"}</div></div>
      <div><div class="label">Phone</div><div class="value">${esc(c.phone) || "-"}</div>
        <div class="label">Admission</div><div class="value">${esc((c.admission_date || "").slice(0, 10))}</div></div>
    </div>
    <h2>Diagnoses</h2>${rows(d.diagnoses, [["code", "Code"], ["description", "Description"], ["status", "Status"]])}
    <h2>Medications</h2>${rows(d.medications, [["name", "Medication"], ["dosage", "Dosage"], ["frequency", "Frequency"], ["status", "Status"]])}
    <h2>Allergies</h2>${rows(d.allergies, [["allergen", "Allergen"], ["reaction", "Reaction"], ["severity", "Severity"]])}
    <h2>Recent Outcome Measures</h2>${rows(d.outcome_measures, [["date", "Date"], ["instrument_name", "Instrument"], [(x) => `${x.score}/${x.max_score}`, "Score"], ["severity", "Severity"]])}
    <h2>Latest ASAM</h2>${d.asam ? rows([d.asam], [["assessment_date", "Date"], [(x) => x.dimensions.map((dd) => dd.rating).join(" · "), "Dimension ratings"], ["recommended_loc", "Recommended LOC"], ["status", "Status"]]) : '<p style="font-size:12px;color:#A1887F">No ASAM assessment on file.</p>'}
    <h2>Treatment Plans</h2>${rows(d.treatment_plans, [[(x) => (x.created_at || "").slice(0, 10), "Created"], ["title", "Title"], ["status", "Status"]])}
    <h2>Upcoming Appointments</h2>${rows(d.upcoming_appointments, [["date", "Date"], ["start_time", "Time"], ["type", "Type"]])}
    <div class="meta">CONFIDENTIAL - Protected Health Information (HIPAA). This document was generated from the Cocoa EHR system and its access has been recorded in the audit log.</div>`);
}

export default function PrintCenter() {
  const clients = useClients();
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  const [letters, setLetters] = useState([]);
  const [faxes, setFaxes] = useState([]);
  useEffect(() => {
    api.get("/letters").then((r) => setLetters(r.data.slice(0, 8))).catch(() => {});
    api.get("/fax-covers").then((r) => setFaxes(r.data.slice(0, 8))).catch(() => {});
  }, []);

  const printChart = async () => {
    if (!clientId) return toast.error("Select a client");
    setBusy(true);
    try {
      const r = await api.get(`/print/chart-summary/${clientId}`);
      printChartSummary(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="print-center-page">
      <PageHeader title="Print Center" subtitle="Print-ready documents - use your browser's Save as PDF for digital copies" />
      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="p-5" data-testid="print-chart-card">
          <h3 className="mb-1 flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><User size={15} /> Client Chart Summary</h3>
          <p className="mb-4 text-xs text-[#7B6157]">Demographics, diagnoses, medications, allergies, outcome measures, ASAM, treatment plans & upcoming visits. Printing is recorded in the audit log.</p>
          <FLabel>Client</FLabel>
          <ClientSelect clients={clients} value={clientId} onChange={setClientId} testId="print-client-select" />
          <Btn className="mt-4 w-full" onClick={printChart} disabled={busy} data-testid="print-chart-btn"><Printer size={14} /> {busy ? "Preparing…" : "Print Chart Summary"}</Btn>
        </Card>
        <Card className="p-5" data-testid="print-letters-card">
          <h3 className="mb-1 flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><FileText size={15} /> Recent Letters</h3>
          <p className="mb-3 text-xs text-[#7B6157]">Generated from Letters & Messaging templates.</p>
          {letters.length === 0 ? <Empty text="No letters yet." /> : letters.map((l) => (
            <div key={l.id} className="flex items-center justify-between border-b border-[#F1EEE7] py-2 last:border-0" data-testid={`print-letter-${l.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-[#3E2723]">{l.subject || l.template_name}</div>
                <div className="text-[11px] text-[#84675B]">{l.client_name} · {dayjs(l.created_at).format("MMM D")}</div>
              </div>
              <button onClick={() => printLetter(l)} data-testid={`print-letter-btn-${l.id}`} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Printer size={15} /></button>
            </div>
          ))}
        </Card>
        <Card className="p-5" data-testid="print-fax-card">
          <h3 className="mb-1 flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><Send size={15} /> Fax Cover Sheets</h3>
          <p className="mb-3 text-xs text-[#7B6157]">HIPAA confidentiality notice included on every cover.</p>
          {faxes.length === 0 ? <Empty text="No fax covers yet." /> : faxes.map((x) => (
            <div key={x.id} className="flex items-center justify-between border-b border-[#F1EEE7] py-2 last:border-0" data-testid={`print-fax-${x.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-[#3E2723]">{x.to_name}</div>
                <div className="text-[11px] text-[#84675B]">{x.re || "-"} · {dayjs(x.created_at).format("MMM D")}</div>
              </div>
              <button onClick={() => printFaxCover(x)} data-testid={`print-fax-btn-${x.id}`} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Printer size={15} /></button>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
